import React, { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import axios from 'axios';
import {
  Warehouse,
  Plus,
  Warning,
  ArrowUp,
  ArrowDown,
  MagnifyingGlass,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

export const InventoryPage = () => {
  const { currentOutlet } = useApp();
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showItemModal, setShowItemModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [itemForm, setItemForm] = useState({
    name: '',
    unit: 'kg',
    current_stock: 0,
    min_stock: 0,
    cost_per_unit: 0,
  });

  const [stockForm, setStockForm] = useState({
    quantity: 0,
    movement_type: 'stock_in',
    notes: '',
  });

  useEffect(() => {
    if (currentOutlet) {
      fetchInventory();
    }
  }, [currentOutlet]);

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/inventory?outlet_id=${currentOutlet.id}`, { withCredentials: true });
      setInventory(response.data);
    } catch (e) {
      console.error('Error fetching inventory:', e);
    }
  };

  const handleCreateItem = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inventory`, {
        ...itemForm,
        outlet_id: currentOutlet.id,
      }, { withCredentials: true });
      await fetchInventory();
      setShowItemModal(false);
      resetItemForm();
    } catch (e) {
      console.error('Error creating item:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleStockUpdate = async () => {
    if (!selectedItem) return;
    setLoading(true);
    try {
      const quantity = stockForm.movement_type === 'stock_out' || stockForm.movement_type === 'wastage'
        ? -Math.abs(stockForm.quantity)
        : Math.abs(stockForm.quantity);
      
      await axios.patch(
        `${API_URL}/api/inventory/${selectedItem.id}/stock?quantity=${quantity}&movement_type=${stockForm.movement_type}&notes=${encodeURIComponent(stockForm.notes)}`,
        {},
        { withCredentials: true }
      );
      await fetchInventory();
      setShowStockModal(false);
      resetStockForm();
    } catch (e) {
      console.error('Error updating stock:', e);
    } finally {
      setLoading(false);
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      unit: 'kg',
      current_stock: 0,
      min_stock: 0,
      cost_per_unit: 0,
    });
  };

  const resetStockForm = () => {
    setStockForm({
      quantity: 0,
      movement_type: 'stock_in',
      notes: '',
    });
    setSelectedItem(null);
  };

  const openStockModal = (item) => {
    setSelectedItem(item);
    setShowStockModal(true);
  };

  const filteredInventory = inventory.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lowStockCount = inventory.filter(item => item.is_low_stock).length;

  const handleLogWastage = async (item) => {
    const qtyStr = window.prompt(`Log wastage for ${item.name} (in ${item.unit}):`, '1');
    if (!qtyStr) return;
    const qty = parseFloat(qtyStr);
    if (Number.isNaN(qty) || qty <= 0) {
      alert('Enter a positive number');
      return;
    }
    const reason = window.prompt('Reason (optional):', 'wastage') || 'wastage';
    try {
      await axios.post(
        `${API_URL}/api/inventory/${item.id}/wastage`,
        { quantity: qty, reason },
        { withCredentials: true }
      );
      fetchInventory();
    } catch (e) {
      alert('Wastage log failed');
    }
  };

  if (!currentOutlet) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Please select an outlet to manage inventory</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="inventory-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold">Inventory</h1>
          <p className="text-muted-foreground mt-1">{currentOutlet?.name}</p>
        </div>
        <Button
          onClick={() => { resetItemForm(); setShowItemModal(true); }}
          className="bg-primary hover:bg-primary-hover text-white"
          data-testid="add-inventory-btn"
        >
          <Plus size={18} className="mr-2" />
          Add Item
        </Button>
      </div>

      {/* Alerts */}
      {lowStockCount > 0 && (
        <div className="bg-accent-yellow/10 border border-accent-yellow/30 rounded-lg p-4 flex items-center gap-3">
          <Warning size={24} className="text-accent-yellow" />
          <span className="font-medium">{lowStockCount} item(s) are running low on stock</span>
        </div>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
        <Input
          placeholder="Search inventory..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-surface border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Item</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Unit</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Current Stock</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Min Stock</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Cost/Unit</th>
              <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredInventory.map((item) => (
              <tr key={item.id} className="hover:bg-surface transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-surface flex items-center justify-center">
                      <Warehouse size={20} className="text-muted-foreground" />
                    </div>
                    <span className="font-medium">{item.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">{item.unit}</td>
                <td className="px-4 py-3 text-right font-medium">{item.current_stock}</td>
                <td className="px-4 py-3 text-right text-sm text-muted-foreground">{item.min_stock}</td>
                <td className="px-4 py-3 text-right text-sm">${item.cost_per_unit.toFixed(2)}</td>
                <td className="px-4 py-3 text-center">
                  {item.is_low_stock ? (
                    <span className="status-badge bg-accent-red/10 text-red-700">Low Stock</span>
                  ) : (
                    <span className="status-badge bg-accent-green/10 text-green-700">In Stock</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleLogWastage(item)}
                      data-testid={`log-wastage-${item.id}`}
                    >
                      Wastage
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openStockModal(item)}
                      data-testid={`update-stock-${item.id}`}
                    >
                      Update Stock
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
            {filteredInventory.length === 0 && (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                  No inventory items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Item Modal */}
      <Dialog open={showItemModal} onOpenChange={setShowItemModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Add Inventory Item</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="Item name"
                data-testid="inventory-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select
                  value={itemForm.unit}
                  onValueChange={(value) => setItemForm({ ...itemForm, unit: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">Kilograms (kg)</SelectItem>
                    <SelectItem value="g">Grams (g)</SelectItem>
                    <SelectItem value="l">Liters (l)</SelectItem>
                    <SelectItem value="ml">Milliliters (ml)</SelectItem>
                    <SelectItem value="pcs">Pieces (pcs)</SelectItem>
                    <SelectItem value="box">Boxes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cost per Unit</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={itemForm.cost_per_unit}
                  onChange={(e) => setItemForm({ ...itemForm, cost_per_unit: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Current Stock</Label>
                <Input
                  type="number"
                  value={itemForm.current_stock}
                  onChange={(e) => setItemForm({ ...itemForm, current_stock: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Minimum Stock</Label>
                <Input
                  type="number"
                  value={itemForm.min_stock}
                  onChange={(e) => setItemForm({ ...itemForm, min_stock: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => setShowItemModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateItem}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={loading || !itemForm.name}
              data-testid="save-inventory-btn"
            >
              {loading ? 'Creating...' : 'Add Item'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Stock Modal */}
      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Update Stock - {selectedItem?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="text-center p-4 bg-surface rounded-lg">
              <p className="text-sm text-muted-foreground">Current Stock</p>
              <p className="text-3xl font-heading font-bold">{selectedItem?.current_stock} {selectedItem?.unit}</p>
            </div>

            <div className="space-y-2">
              <Label>Movement Type</Label>
              <Select
                value={stockForm.movement_type}
                onValueChange={(value) => setStockForm({ ...stockForm, movement_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stock_in">Stock In</SelectItem>
                  <SelectItem value="stock_out">Stock Out</SelectItem>
                  <SelectItem value="purchase">Purchase</SelectItem>
                  <SelectItem value="wastage">Wastage</SelectItem>
                  <SelectItem value="adjustment">Adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quantity</Label>
              <Input
                type="number"
                value={stockForm.quantity}
                onChange={(e) => setStockForm({ ...stockForm, quantity: parseFloat(e.target.value) || 0 })}
                placeholder="0"
                data-testid="stock-quantity"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={stockForm.notes}
                onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={() => { setShowStockModal(false); resetStockForm(); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleStockUpdate}
              className="flex-1 bg-primary hover:bg-primary-hover text-white"
              disabled={loading || stockForm.quantity === 0}
              data-testid="update-stock-btn"
            >
              {loading ? 'Updating...' : 'Update Stock'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryPage;
